import { createFileRoute, useNavigate } from "@tanstack/react-router";

export const Route = createFileRoute(
  "/admin/_layout/inventory/rental-contracts"
)({
  component: RouteComponent,
});

function RouteComponent() {
  // RentalLeaseContractViewSet's read_permission ("inventory.stock.view")
  // gates list/retrieve too, with no degrade-to-own-rows fallback, so the
  // whole page is protected up front; write_permission
  // ("inventory.rental.manage") gates just the write UI within.
  return (
    <ProtectedRoute requiredPermission="inventory.stock.view">
      <RentalContractsPage />
    </ProtectedRoute>
  );
}

import { useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { ProtectedRoute } from "@/components/protected-route";
import { SidebarInset } from "@/components/ui/sidebar";
import { Input } from "@/components/ui/input";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { LocalSearchSelect } from "@/components/reusable-components/local-search-select";
import { AccountSearchSelect } from "@/components/crm/shared/AccountSearchSelect";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ConfirmDialog } from "@/components/crud/ConfirmDialog/ConfirmDialog";
import {
  useFetchRentalContracts,
  useRentalContractMutations,
} from "@/hooks/api/inventory/useRentalContracts";
import {
  useFetchItems,
  useFetchWarehouses,
  ALL_ACTIVE_PARAMS,
} from "@/hooks/api/inventory/useCatalog";
import { useFetchInspectionChecklists } from "@/hooks/api/inventory/useCollectionsLabels";
import {
  useFetchAssetItems,
  useAssetItemMutations,
  ALL_ASSET_ITEMS_PAGE_SIZE,
} from "@/hooks/api/inventory/useAssetItems";
import { RentalContractTable } from "@/components/inventory/tables/RentalContractTable";
import { RentalContractFormSheet } from "@/components/inventory/forms/RentalContractFormSheet";
import { RentalPartialReturnModal } from "@/components/inventory/forms/RentalPartialReturnModal";
import { RentalCheckoutModal } from "@/components/inventory/forms/RentalCheckoutModal";
import { InspectReturnModal } from "@/components/inventory/forms/InspectReturnModal";
import { RentalContractDetailSheet } from "@/components/inventory/forms/DetailViewSheet";
import type {
  InspectReturnPayload,
  RentalLeaseContract,
  RentalLeaseContractPayload,
  RentalPartialCheckoutPayload,
  RentalPartialReturnPayload,
} from "@/types/inventory";
import { useMyPermissions } from "@/hooks/api/authorization/useAuthorization";
import { can } from "@/lib/permissions";
import { RETURN_INSPECTION_ELIGIBLE_STATUSES } from "@/lib/inventory";

const RENTAL_CONTRACT_STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Reserved", value: "RESERVED" },
  { label: "Active", value: "ACTIVE" },
  { label: "Partially Returned", value: "PARTIALLY_RETURNED" },
  { label: "Overdue", value: "OVERDUE" },
  { label: "Closed", value: "CLOSED" },
  { label: "Cancelled", value: "CANCELLED" },
];

function RentalContractsPage() {
  const navigate = useNavigate();
  const { data: capabilities } = useMyPermissions();
  const canManage = can(capabilities, "inventory.rental.manage");
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();

  const [showFilters, setShowFilters] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | undefined>(
    undefined
  );
  const [warehouseFilter, setWarehouseFilter] = useState<string | undefined>(
    undefined
  );
  const [customerFilter, setCustomerFilter] = useState<string | undefined>(
    undefined
  );

  const resetFilters = () => {
    setSearchFilter("");
    setStatusFilter(undefined);
    setWarehouseFilter(undefined);
    setCustomerFilter(undefined);
    setPage(1);
  };

  const contractsQuery = useFetchRentalContracts({
    page_size: pageSize,
    page,
    search: searchFilter || undefined,
    status: statusFilter,
    source_warehouse: warehouseFilter ? parseInt(warehouseFilter) : undefined,
    customer: customerFilter ? parseInt(customerFilter) : undefined,
  });
  const contracts = contractsQuery.data?.results ?? [];
  const count = contractsQuery.data?.count ?? 0;

  const itemsQuery = useFetchItems(ALL_ACTIVE_PARAMS);
  const items = itemsQuery.data?.results ?? [];
  const warehousesQuery = useFetchWarehouses(ALL_ACTIVE_PARAMS);
  const warehouses = warehousesQuery.data?.results ?? [];
  const checklistsQuery = useFetchInspectionChecklists(ALL_ACTIVE_PARAMS);
  const checklists = checklistsQuery.data?.results ?? [];
  const {
    createContract,
    updateContract,
    deleteContract,
    reserveContract,
    partialCheckoutContract,
    partialReturnContract,
    cancelContract,
  } = useRentalContractMutations();
  const { bulkInspectReturn } = useAssetItemMutations();

  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editing, setEditing] = useState<RentalLeaseContract | null>(null);
  const [viewing, setViewing] = useState<RentalLeaseContract | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RentalLeaseContract | null>(
    null
  );
  const [returnTarget, setReturnTarget] = useState<RentalLeaseContract | null>(
    null
  );
  const [inspectTarget, setInspectTarget] =
    useState<RentalLeaseContract | null>(null);
  const [checkoutTarget, setCheckoutTarget] =
    useState<RentalLeaseContract | null>(null);
  const [reserveTarget, setReserveTarget] =
    useState<RentalLeaseContract | null>(null);
  const [cancelTarget, setCancelTarget] = useState<RentalLeaseContract | null>(
    null
  );

  const inspectAssetItemsQuery = useFetchAssetItems(
    {
      rental_contract: inspectTarget?.id,
      status_in: [...RETURN_INSPECTION_ELIGIBLE_STATUSES],
      page_size: ALL_ASSET_ITEMS_PAGE_SIZE,
    },
    { enabled: !!inspectTarget }
  );
  const inspectAssetItems = inspectAssetItemsQuery.data?.results ?? [];

  const isMutating =
    createContract.isPending ||
    updateContract.isPending ||
    deleteContract.isPending ||
    reserveContract.isPending ||
    partialCheckoutContract.isPending ||
    partialReturnContract.isPending ||
    cancelContract.isPending;
  const isLoading = contractsQuery.isPending || isMutating;
  const isFetching = contractsQuery.isFetching || isMutating;

  const handleSubmit = async (data: RentalLeaseContractPayload) => {
    if (editing) {
      await updateContract.mutateAsync({ id: editing.id, payload: data });
      toast.success("Contract updated");
    } else {
      await createContract.mutateAsync(data);
      toast.success("Contract created");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleReserve = (contract: RentalLeaseContract) => {
    setReserveTarget(contract);
  };

  const handleReserveConfirm = async () => {
    if (!reserveTarget) return;
    try {
      await reserveContract.mutateAsync({ id: reserveTarget.id });
      toast.success("Contract reserved");
      setReserveTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handlePartialCheckout = async (
    payload: RentalPartialCheckoutPayload
  ) => {
    if (!checkoutTarget) return;
    await partialCheckoutContract.mutateAsync({
      id: checkoutTarget.id,
      payload,
    });
    toast.success("Items checked out successfully");
    setCheckoutTarget(null);
  };

  const handlePartialReturn = async (payload: RentalPartialReturnPayload) => {
    if (!returnTarget) return;
    await partialReturnContract.mutateAsync({ id: returnTarget.id, payload });
    toast.success("Return processed");
    setReturnTarget(null);
  };

  const handleReturnInspect = async (
    payload: InspectReturnPayload,
    assetItemIds?: number[]
  ) => {
    if (!assetItemIds?.length) return;
    await bulkInspectReturn.mutateAsync({ assetItemIds, payload });
    toast.success("Return Inspection recorded");
    setInspectTarget(null);
  };

  const handleCancel = (contract: RentalLeaseContract) => {
    setCancelTarget(contract);
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelContract.mutateAsync({ id: cancelTarget.id });
      toast.success("Contract cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteContract.mutateAsync({ id: deleteTarget.id });
      toast.success("Contract deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />

      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Rental Contracts"
          description="Manage asset rental and lease agreements with customers."
          isLoading={isFetching}
          onToggleFilter={() => setShowFilters((v) => !v)}
          onAdd={
            canManage
              ? () => {
                  setEditing(null);
                  setIsSheetOpen(true);
                }
              : undefined
          }
          addLabel="Contract"
        />

        <FiltersPanel
          show={showFilters}
          onClear={resetFilters}
          isLoading={isFetching}
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by contract number..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="h-9 pl-8"
            />
          </div>
          <SelectFilter
            placeholder="Status"
            options={RENTAL_CONTRACT_STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => {
              setStatusFilter(v);
              setPage(1);
            }}
          />
          <LocalSearchSelect
            className="w-fit"
            items={warehouses}
            keyExtractor={(w) => w.id}
            labelExtractor={(w) => w.name}
            watchedValue={warehouseFilter ?? null}
            onSelect={(v) => {
              setWarehouseFilter(v == null ? undefined : String(v));
              setPage(1);
            }}
            placeholder="Warehouse"
            searchPlaceholder="Search warehouses..."
            noItemsMessage="No warehouses found."
          />
          <AccountSearchSelect
            className="w-fit"
            value={customerFilter ?? null}
            onChange={(v) => {
              setCustomerFilter(v == null ? undefined : String(v));
              setPage(1);
            }}
            placeholder="Customer"
          />
        </FiltersPanel>

        <RentalContractTable
          contracts={contracts}
          isLoading={isLoading}
          onNameClick={(c) =>
            navigate({
              to: "/admin/inventory/rental-contracts/$id",
              params: { id: String(c.id) },
            })
          }
          onView={(c) => setViewing(c)}
          onEdit={(c) => {
            setEditing(c);
            setIsSheetOpen(true);
          }}
          onReserve={handleReserve}
          onCheckout={(c) => setCheckoutTarget(c)}
          onPartialReturn={(c) => setReturnTarget(c)}
          onReturnInspect={(c) => setInspectTarget(c)}
          onCancel={handleCancel}
          onDelete={(c) => setDeleteTarget(c)}
          canManage={canManage}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <RentalContractDetailSheet
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        contract={viewing}
        onReturnInspect={(c) => setInspectTarget(c)}
      />

      <InspectReturnModal
        isOpen={!!inspectTarget}
        onClose={() => setInspectTarget(null)}
        onSubmit={handleReturnInspect}
        assetItem={null}
        contextLabel={
          <>
            Contract:{" "}
            <span className="font-mono font-medium text-foreground">
              {inspectTarget?.contract_number}
            </span>
          </>
        }
        assetItems={inspectAssetItems}
        requireAssetSelection
        checklists={checklists}
        isLoading={bulkInspectReturn.isPending}
      />

      <RentalContractFormSheet
        isOpen={isSheetOpen}
        onClose={() => {
          setIsSheetOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSubmit}
        editing={editing}
        items={items}
        warehouses={warehouses}
        isLoading={createContract.isPending || updateContract.isPending}
      />

      <RentalCheckoutModal
        isOpen={!!checkoutTarget}
        onClose={() => setCheckoutTarget(null)}
        onSubmit={handlePartialCheckout}
        contract={checkoutTarget}
        isLoading={partialCheckoutContract.isPending}
      />

      <RentalPartialReturnModal
        isOpen={!!returnTarget}
        onClose={() => setReturnTarget(null)}
        onSubmit={handlePartialReturn}
        contract={returnTarget}
        isLoading={partialReturnContract.isPending}
      />

      <DeleteConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Contract"
        description={`Delete contract "${deleteTarget?.contract_number}"? This cannot be undone.`}
        itemName={deleteTarget?.contract_number}
        isLoading={deleteContract.isPending}
        destructive
      />

      <DeleteConfirmDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        title="Cancel Contract"
        description="Cancel this rental contract? This cannot be undone."
        itemName={cancelTarget?.contract_number}
        isLoading={cancelContract.isPending}
        confirmLabel="Cancel Contract"
        loadingLabel="Cancelling..."
        destructive
      />

      <ConfirmDialog
        isOpen={!!reserveTarget}
        onClose={() => setReserveTarget(null)}
        onConfirm={handleReserveConfirm}
        isLoading={reserveContract.isPending}
        title="Reserve Contract"
        description={`Reserve contract "${reserveTarget?.contract_number}"? Stock will be reserved from the source warehouse.`}
        confirmLabel="Reserve"
        loadingLabel="Reserving..."
      />
    </SidebarInset>
  );
}
